import React, { useState } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MuiButton from '@mui/material/Button';
import LandingLayout from './components/LandingLayout';
import Section from './components/Section';

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    role: '',
    message: '',
  });

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${process.env.REACT_APP_API}/contact`, form);
      setSubmitted(true);
    } catch (err: any) {
      if (err?.response?.status === 429) {
        setError('Too many submissions — please wait a minute and try again.');
      } else {
        setError('Something went wrong. Please try again or email daniel@joinpressto.com directly.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isValid = form.name && form.email && form.message;

  return (
    <LandingLayout>
      <Section background="#F8FAFD" py={{ xs: 8, md: 12 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: 4, md: 8 },
            maxWidth: 1000,
            mx: 'auto',
          }}
        >
          {/* Left: info */}
          <Box>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2rem', md: '2.75rem' },
                fontWeight: 800,
                color: '#163567',
                mb: 2,
              }}
            >
              Let&apos;s Talk
            </Typography>
            <Typography
              sx={{
                fontSize: '1.05rem',
                color: '#555',
                lineHeight: 1.7,
                mb: 4,
              }}
            >
              Interested in seeing if Pressto is a good fit for your school or district?
              Fill out the form and we&apos;ll set up a discovery call.
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#163567', mb: 0.5 }}>
                Email
              </Typography>
              <Typography sx={{ fontSize: '0.9rem', color: '#555' }}>
                daniel@joinpressto.com
              </Typography>
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#163567', mb: 1 }}>
                Trusted by
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', color: '#888', lineHeight: 1.8 }}>
                NYC Department of Education, LA Unified, Chicago Public Schools, Houston ISD, and 100+ more districts across North America.
              </Typography>
            </Box>
          </Box>

          {/* Right: form */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: '16px',
              backgroundColor: '#FFFFFF',
              border: '1px solid #E8EDF5',
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            }}
          >
            {submitted ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography sx={{ fontSize: '2rem', mb: 2 }}>&#10003;</Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', color: '#163567', mb: 1 }}>
                  Thank you!
                </Typography>
                <Typography sx={{ fontSize: '0.9rem', color: '#555' }}>
                  We&apos;ll be in touch soon.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {error && (
                  <Typography sx={{ fontSize: '0.85rem', color: '#c62828', backgroundColor: '#ffebee', padding: '10px 12px', borderRadius: '6px' }}>
                    {error}
                  </Typography>
                )}
                <TextField
                  label="Name"
                  value={form.name}
                  onChange={handleChange('name')}
                  fullWidth
                  required
                  size="small"
                />
                <TextField
                  label="Email"
                  type="email"
                  value={form.email}
                  onChange={handleChange('email')}
                  fullWidth
                  required
                  size="small"
                />
                <TextField
                  label="Role"
                  value={form.role}
                  onChange={handleChange('role')}
                  fullWidth
                  size="small"
                  placeholder="e.g. Teacher, Principal, Curriculum Director"
                />
                <TextField
                  label="Message"
                  value={form.message}
                  onChange={handleChange('message')}
                  fullWidth
                  required
                  multiline
                  rows={4}
                  size="small"
                />
                <MuiButton
                  type="submit"
                  variant="contained"
                  disabled={!isValid || submitting}
                  sx={{
                    backgroundColor: '#163567',
                    color: '#FFFFFF',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    textTransform: 'none',
                    borderRadius: '10px',
                    py: 1.5,
                    '&:hover': { backgroundColor: '#0B1F40' },
                    '&.Mui-disabled': { opacity: 0.5 },
                  }}
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </MuiButton>
              </Box>
            )}
          </Box>
        </Box>
      </Section>
    </LandingLayout>
  );
}
